import React from 'react';
import { ShippingStatusFormStyled } from './styled';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import moment from 'moment';
import 'moment-timezone';
import { Buttons, Inputs } from 'components';
import { REWARD_RENDER_TYPE } from 'utils/functions/reward';
import {
  SHIPPING_STATUS,
  SHIPPING_STATUS_LABEL,
  SHIPPING_STATUS_OPTIONS,
  SHIPPING_STATUS_REQUIRE_REASON,
  CURRENT_SHIPPING_STATUS,
  REASON_MAX_LENGTH,
} from 'utils/functions/shipping';

const ShippingStatusForm = ({ data, onSubmit, onCancel, disabled }) => {
  // yup 0.32 - then/otherwise รับ schema ไม่ใช่ฟังก์ชันแบบ 1.x
  const schema = yup.object().shape({
    shipping_status: yup.string().required('กรุณาเลือกสถานะ'),
    reason: yup.string().when('shipping_status', {
      is: (value) => SHIPPING_STATUS_REQUIRE_REASON.indexOf(value) > -1,
      then: yup
        .string()
        .trim()
        .required('กรุณาระบุเหตุผล')
        .max(REASON_MAX_LENGTH, `เหตุผลยาวเกิน ${REASON_MAX_LENGTH} ตัวอักษร`),
      otherwise: yup.string().max(REASON_MAX_LENGTH),
    }),
  });

  const currentStatus = CURRENT_SHIPPING_STATUS(data && data.shipping_status);
  const currentReason = (data && data.shipping_status_reason) || '';

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      // '9' (ไม่ระบุ) ตั้งไม่ได้ ถ้าของเดิมเป็น '9' ให้เริ่มที่ 'รอดำเนินการจัดส่ง'
      shipping_status:
        currentStatus === SHIPPING_STATUS.LEGACY
          ? SHIPPING_STATUS.PENDING
          : currentStatus,
      reason: currentReason,
    },
  });

  const selected = watch('shipping_status');
  const needReason = SHIPPING_STATUS_REQUIRE_REASON.indexOf(selected) > -1;

  const info = (label, value) => (
    <div className="info_item">
      <div className="info_label">{label}</div>
      <div className="info_value">{value || '-'}</div>
    </div>
  );

  return (
    <ShippingStatusFormStyled>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* บอกแค่พอให้รู้ว่ากำลังแก้แถวไหน รายละเอียดเต็มอยู่ด้านล่าง
            เอาช่องที่ต้องกรอกขึ้นบนสุด ไม่งั้นเมนู dropdown กางแล้วพ้นขอบ modal */}
        <div className="identity">
          <span className="who">{data.customer_user_id}</span>
          <span className="dot">·</span>
          <span>{data.reward_name}</span>
          {data.reward_redeem_amount > 1 && (
            <span className="qty">{data.reward_redeem_amount} ชิ้น</span>
          )}
        </div>

        <div className="status_row">
          <div className="from_block">
            <div className="from_label">สถานะปัจจุบัน</div>
            <div className="from_value">
              {SHIPPING_STATUS_LABEL[currentStatus] || '-'}
            </div>
          </div>
          {/* ห้ามตั้งชื่อคลาสนี้ว่า arrow เพราะ Inputs.Dropdown ใช้ .arrow
              ห่อไอคอนลูกศรของตัวเองอยู่ กฎที่เขียนไว้จะไปโดนไอคอนนั้นด้วย */}
          <div className="status_arrow">→</div>
          <div className="to_block">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.Dropdown
                  theme_standard
                  {...field}
                  label="เปลี่ยนเป็น"
                  options={SHIPPING_STATUS_OPTIONS}
                  placeholder="เลือกสถานะ"
                  errors={errors.shipping_status?.message}
                />
              )}
              name="shipping_status"
            />
          </div>
        </div>

        {needReason ? (
          <div className="input_wrap">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.TextArea
                  theme_standard
                  {...field}
                  rows={3}
                  label={`เหตุผล (จำเป็นสำหรับสถานะ "${SHIPPING_STATUS_LABEL[selected]}")`}
                  placeholder="เช่น ที่อยู่ไม่ถูกต้อง ติดต่อลูกค้าไม่ได้"
                  errors={errors.reason?.message}
                />
              )}
              name="reason"
            />
          </div>
        ) : (
          // ช่องเหตุผลหายไปจากจอตอนเลือกสถานะที่ไม่ต้องใช้เหตุผล
          // ถ้าไม่บอกอะไรเลย ผู้ใช้จะไม่รู้ว่าเหตุผลเดิมถูกล้าง
          currentReason && (
            <div className="notice warn">
              สถานะนี้ไม่ต้องใช้เหตุผล — เหตุผลเดิม "{currentReason}"
              จะถูกล้าง (ยังดูย้อนหลังได้ในประวัติ)
            </div>
          )
        )}

        {selected === SHIPPING_STATUS.CANCELLED && (
          <div className="notice stop">
            "ยกเลิก" บันทึกสถานะการจัดส่งเท่านั้น
            ไม่มีการคืนแต้มและไม่คืนสต็อกให้ลูกค้า
          </div>
        )}

        <div className="action_row">
          <div className="btn_item">
            <Buttons.BgStandard
              theme_gray
              label="ยกเลิก"
              onClick={onCancel}
              disabled={disabled}
            />
          </div>
          <div className="btn_item">
            <Buttons.BgStandard
              type="submit"
              label="บันทึก"
              disabled={disabled}
            />
          </div>
        </div>

        {/* ข้อมูลอ้างอิง ไว้ตรวจว่าแก้ถูกแถวและดูที่อยู่ตอนเขียนเหตุผล
            วางไว้ล่างสุดเพราะไม่ใช่สิ่งที่ต้องกรอก */}
        <div className="detail_block">
          <div className="detail_head">ข้อมูลรายการ</div>
          <div className="detail_grid">
            {info('Redeem type', REWARD_RENDER_TYPE(data.reward_type))}
            {info('Coupon code', data.reward_code)}
            {info(
              'Created at',
              data.created_at
                ? moment(data.created_at)
                    .tz('Asia/Bangkok')
                    .format('DD/MM/YYYY HH:mm:ss')
                : '-'
            )}
            {info('Qty', data.reward_redeem_amount)}
            {info('ผู้รับ', data.name_shipping)}
            {info('โทร', data.phone_shipping)}
            <div className="info_item full">
              <div className="info_label">ที่อยู่</div>
              <div className="info_value">{data.address_shipping || '-'}</div>
            </div>
          </div>
        </div>
      </form>
    </ShippingStatusFormStyled>
  );
};

ShippingStatusForm.propTypes = {};

export default ShippingStatusForm;
